/* eslint-disable max-len */
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	computed,
	EventEmitter,
	input,
	Input,
	Output,
	Signal,
	inject,
	signal,
	WritableSignal,
	OnChanges,
	SimpleChanges,
	effect,
	OnInit,
	model
} from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { IonicModule, ModalController } from "@ionic/angular";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { DateTime } from "luxon";
import { map, of, switchMap, take } from "rxjs";
import { TypeModule } from "src/app/shared/enums/type-module";
import {
	ICheckin,
	ICheckinChecked,
	ICustomFieldData,
	IEvent,
	IEventUser,
	IFullCustomField,
	IModule,
	IModuleCustomField
} from "src/app/shared/interfaces";
import { ICardExchangeForm } from "src/app/shared/interfaces/card-exchange.interfaces";
import { IEventUserUpdatedSettings } from "src/app/shared/interfaces/event-users.interfaces";
import { IFavoriteFolder } from "src/app/shared/interfaces/folders.interfaces";
import {
	AnalyticsService,
	CardExchangeService,
	CustomFieldsService,
	EventUsersService,
	FirestoreService,
	UtilityService
} from "src/app/shared/services";
import { environment } from "src/environments/environment";
import { CardExchangeFormComponent } from "../../components/card-exchange-form/card-exchange-form.component";
import { ObserveVisibilityDirective } from "src/app/shared/directives/observe-visibility";
import { CommonModule } from "@angular/common";
import { Store } from "@ngrx/store";
import { getSpecificsTracks } from "src/app/shared/selectors/generics-modules-data.selectors";
import { LocalStorageService } from "src/app/shared/services/local-storage.service";
import { Platform } from "@ionic/angular";
import { FavoritesService } from "src/app/shared/services/favorites.service";
import * as _ from "lodash";
import { LogFuncArgs } from "src/app/shared/utils-func/debug-decorators";

@Component({
	selector: "app-event-user-item",
	templateUrl: "./event-user-item.component.html",
	styleUrls: ["./event-user-item.component.scss"],
	imports: [CommonModule, IonicModule, ObserveVisibilityDirective, TranslateModule],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true
})
export class EventUserItemComponent implements OnChanges, OnInit {
	@Input() designType: "card" | "row" | "column" = "row";

	@Input() rootElement: Element;
	@Input() event: IEvent;
	@Input() mainModule: IModule;
	@Input() targetModule: IModule;
	@Input() customFields: IFullCustomField[] = [];
	@Input() groupedLetters: any = null;
	@Input() eventUser: IEventUser;
	@Input() data: IEventUser;
	@Input() favoriteFolder: IFavoriteFolder;
	@Input() favoriteModule: IModule;
	@Input({ required: false }) cardExchangeForm: ICardExchangeForm;
	@Input({ required: false }) cardExchangeModule: IModule;
	currentLanguage = model<string>(environment.platform.defaultLanguage);
	private dataSignal: WritableSignal<IEventUser | null> = signal(null);
	private customFieldsSignal: WritableSignal<IFullCustomField[]> = signal([]);

	// Refresh trigger for reactive updates
	private refreshTrigger: WritableSignal<number> = signal(0);

	@Input() networkStatus: boolean = true;
	showTags = input<boolean>(false);

	@Input() checkin: ICheckin;
	@Input() isChecked: boolean = false;
	@Input() checked: ICheckinChecked = null;
	@Output() changeStatus: EventEmitter<IEventUser> = new EventEmitter();
	@Output() modifyMultiCheck: EventEmitter<{ data: IEventUser; type: "add" | "remove" }> = new EventEmitter();

	moduleCustomFields: Signal<IModuleCustomField[]> = computed(() =>
		this.customFields.map((cus) => cus.moduleSettings)
	);
	connectedStatusGetted: boolean = false;
	isConnected: boolean = false;

	updating: boolean = false;

	isMobile = window.innerWidth < 768 ? true : false;

	typesModules = TypeModule;

	// card exchange
	eventUserStates: { [key: string]: boolean } = {};

	// --- Injected Services ---
	private SEventUsers = inject(EventUsersService);
	private STranslate = inject(TranslateService);
	private modalCtrl = inject(ModalController);
	private snackbar = inject(MatSnackBar);
	private platform = inject(Platform);
	private favoritesService = inject(FavoritesService);
	private localStorageService = inject(LocalStorageService);
	private store = inject(Store);
	private SCardExchange = inject(CardExchangeService);
	// --- End Injected Services ---

	// Computed signal for tags data
	tagsData: Signal<any[]> = computed(() => {
		const currentLang = this.currentLanguage();
		const data = this.dataSignal();
		console.warn("🚀 ~ EventUserItemComponent ~ tagsData ~ currentLang:", currentLang);
		console.warn("🚀 ~ EventUserItemComponent ~ tagsData ~ data:", data);
		// Trigger refresh to ensure reactivity
		this.refreshTrigger();

		if (!data?.tags?.length) return [];

		// This needs to be synchronous, so we'll store the result in another signal
		this.loadTagsData(data.tags, currentLang);
		return this.cachedTagsData();
	});

	private cachedTagsData: WritableSignal<any[]> = signal([]);

	// Computed signal for custom fields tags
	customFieldsTags: Signal<ICustomFieldData[]> = computed(() => {
		const currentLang = this.currentLanguage();
		const data = this.dataSignal();
		const customFields = this.customFieldsSignal();
		console.warn("🚀 ~ EventUserItemComponent ~ customFieldsTags ~ currentLang:", currentLang);
		// Trigger refresh to ensure reactivity
		this.refreshTrigger();

		if (!data?.customFields || !customFields.length) return [];

		return data.customFields
			.filter((eventUserCustomField) => {
				return (
					(eventUserCustomField &&
						this.customFields.find(
							(computedCustomField) =>
								eventUserCustomField &&
								computedCustomField &&
								computedCustomField.baseSettings &&
								computedCustomField.baseSettings.uid === eventUserCustomField.uid &&
								computedCustomField.moduleSettings.canBeTag
						) &&
						((eventUserCustomField.field.text &&
							eventUserCustomField.field.text.trim() &&
							eventUserCustomField.field.text?.trim() !== "") ||
							(eventUserCustomField.field.multiLanguageText &&
								eventUserCustomField.field.multiLanguageText[this.currentLanguage()]?.trim() &&
								eventUserCustomField.field.multiLanguageText[this.currentLanguage()]?.trim() !== "") ||
							(eventUserCustomField.field.numeric && eventUserCustomField.field.numeric !== -1))) ||
					(eventUserCustomField.field.list && eventUserCustomField.field.list.selectedOptions.length > 0)
				);
			})
			.sort((a, b) =>
				this.getCustomFieldOrder(a) > this.getCustomFieldOrder(b)
					? 1
					: this.getCustomFieldOrder(a) < this.getCustomFieldOrder(b)
						? -1
						: 0
			);
	});

	private loadTagsData(tagIds: string[], currentLang: string): void {
		console.warn("🚀 ~ EventUserItemComponent ~ loadTagsData ~ tagIds:", tagIds);
		this.store
			.select(getSpecificsTracks(tagIds))
			.pipe(
				take(1),
				switchMap((tags) => {
					console.warn("🚀 ~ EventUserItemComponent ~ loadTagsData ~ tags:", tags);
					return of(
						tags.sort((a, b) =>
							a.name[currentLang] > b.name[currentLang]
								? 1
								: a.name[currentLang] < b.name[currentLang]
									? -1
									: 0
						)
					);
				})
			)
			.subscribe((sortedTags) => {
				this.cachedTagsData.set(sortedTags);
				this.cdr.markForCheck();
			});
	}

	constructor(
		private cdr: ChangeDetectorRef,
		private SFirestore: FirestoreService,
		public SUtility: UtilityService,
		public SCustomFields: CustomFieldsService,
		private SAnalytics: AnalyticsService
	) {
		// Effect to trigger refresh when language changes
		effect(() => {
			const currentLang = this.currentLanguage();
			console.warn("🚀 ~ EventUserItemComponent ~ language change effect ~ currentLang:", currentLang);
			// Increment refresh trigger to force computed signals to recalculate
			this.refreshTrigger.update((v) => v + 1);
			// Force change detection
			this.cdr.markForCheck();
		});
	}

	ngOnInit(): void {
		// Initialize signals with input values
		this.dataSignal.set(this.data);
		this.customFieldsSignal.set(this.customFields);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes["data"]) {
			this.dataSignal.set(changes["data"].currentValue);
		}

		if (changes["customFields"]) {
			this.customFieldsSignal.set(changes["customFields"].currentValue || []);
		}
	}

	onVisibleEventUser(evt: { target: HTMLElement; state: "ENTER" | "LEAVE"; scrollDirection: "UP" | "DOWN" }) {
		if (evt.state === "ENTER") {
			// let checkAlreadyGetSetting = this.mappedEventUsersConnectedStatus[eventUser.uid];

			if (this.isConnected || !this.networkStatus) {
				return;
			} else {
				this.SFirestore.getDocumentObs(
					`events/${this.event.uid}/modules/${this.data.moduleId}/event-users-updated-settings/${this.data.uid}`
				)
					.pipe(
						take(1),
						map((doc) => doc.data() as IEventUserUpdatedSettings)
					)
					.subscribe((updatedSettings) => {
						this.isConnected = updatedSettings?.connected;
					});
			}
		}
	}

	/**
	 * getCustomFieldOrder
	 */
	getCustomFieldOrder(customField: ICustomFieldData): number {
		const customFields = this.customFieldsSignal();
		return customFields.find((cus) => cus.baseSettings?.uid === customField.uid)
			? customFields.find((cus) => cus.baseSettings?.uid === customField.uid)?.moduleSettings?.order
			: -1;
	}

	/**
	 * checkIsFavorite
	 * @returns boolean
	 */
	checkIsFavorite(): boolean {
		if (this.eventUser) {
			return this.eventUser.favorites?.includes(this.data.uid);
		} else {
			const guestFavorites = this.localStorageService.getItem<string[]>("guestFavorites");
			return guestFavorites?.includes(this.data.uid) ?? false;
		}
	}

	// async changeFavorites(eventUserId: string, type: boolean) {
	// 	try {
	// 		if (!this.updating) {
	// 			this.updating = true;
	// 			if (this.eventUser) {
	// 				if (this.eventUser.favorites) {
	// 					if (type && !this.eventUser.favorites.includes(eventUserId)) {
	// 						this.eventUser.favorites.push(eventUserId);
	// 					} else {
	// 						this.eventUser.favorites = this.eventUser.favorites.filter((fav) => fav !== eventUserId);
	// 					}
	// 				} else {
	// 					this.eventUser["favorites"] = [eventUserId];
	// 				}
	// 				await this.SEventUsers.updatePartOfEventUser(
	// 					this.eventUser.eventId,
	// 					this.eventUser.moduleId,
	// 					this.eventUser.uid,
	// 					{
	// 						favorites: this.eventUser.favorites
	// 					}
	// 				);
	// 				this.snackbar.open(this.STranslate.instant("snackbar.update_successfull"), null, {
	// 					duration: 3000,
	// 					panelClass: ["success-snackbar"]
	// 				});
	// 				this.updating = false;
	// 				this.cdr.markForCheck();
	// 			}
	// 		}
	// 	} catch (error) {
	// 		// Snackbar error
	// 		this.snackbar.open(this.STranslate.instant("snackbar.error_occured"), null, {
	// 			duration: 3000,
	// 			panelClass: ["error-snackbar"]
	// 		});
	// 		this.updating = false;
	// 		this.cdr.markForCheck();
	// 	}
	// }

	@LogFuncArgs
	async changeFavorites(eventUserId: string, add: boolean): Promise<void> {
		if (this.updating) {
			return;
		}
		this.updating = true;

		try {
			if (this.eventUser) {
				const currentFavorites = this.eventUser.favorites ? [...this.eventUser.favorites] : [];
				let updatedFavorites: string[];

				if (add) {
					if (!currentFavorites.includes(eventUserId)) {
						updatedFavorites = [...currentFavorites, eventUserId];
					} else {
						updatedFavorites = currentFavorites;
					}
				} else {
					updatedFavorites = currentFavorites.filter((fav) => fav !== eventUserId);
				}

				if (!_.isEqual(currentFavorites, updatedFavorites)) {
					this.eventUser.favorites = updatedFavorites;
					this.cdr.markForCheck();

					await this.SEventUsers.updatePartOfEventUser(
						this.eventUser.eventId,
						this.eventUser.moduleId,
						this.eventUser.uid,
						{
							favorites: updatedFavorites
						}
					);
					this.updateFavoriteAnalytics(add);
					this.snackbar.open(
						add
							? this.STranslate.instant("snackbar.add_to_my_favorites")
							: this.STranslate.instant("snackbar.removed_from_favorites"),
						null,
						{
							duration: 3000,
							panelClass: ["success-snackbar"]
						}
					);
				} else {
					this.updating = false;
					return;
				}
			} else {
				const guestFavorites = this.localStorageService.getItem<string[]>("guestFavorites") ?? [];
				let updatedGuestFavorites: string[];

				if (add) {
					if (!guestFavorites.includes(eventUserId)) {
						updatedGuestFavorites = [...guestFavorites, eventUserId];
					} else {
						updatedGuestFavorites = guestFavorites;
					}
				} else {
					updatedGuestFavorites = guestFavorites.filter((fav) => fav !== eventUserId);
				}

				if (!_.isEqual(guestFavorites, updatedGuestFavorites)) {
					this.localStorageService.setItem("guestFavorites", updatedGuestFavorites);
					this.updateFavoriteAnalytics(add);
					this.snackbar.open(
						add
							? this.STranslate.instant("snackbar.add_to_my_favorites")
							: this.STranslate.instant("snackbar.removed_from_favorites"),
						null,
						{
							duration: 3000,
							panelClass: ["success-snackbar"]
						}
					);
					this.cdr.markForCheck();
				} else {
					this.updating = false;
					return;
				}
			}

			this.snackbar.open(this.STranslate.instant("snackbar.update_successfull"), null, {
				duration: 3000,
				panelClass: ["success-snackbar"]
			});
		} catch (error) {
			console.error("Error changing favorites:", error);
			this.snackbar.open(this.STranslate.instant("snackbar.error_occured"), null, {
				duration: 3000,
				panelClass: ["error-snackbar"]
			});
		} finally {
			this.updating = false;
			this.cdr.markForCheck();
		}
	}

	checkIsNew() {
		return (
			DateTime.fromISO(this.data.creationDate) >
			DateTime.local().minus({
				minute:
					this.mainModule.options && this.mainModule.options.newHintChipDelay
						? this.mainModule.options.newHintChipDelay
						: 1440
			})
		);
	}

	checkIsContact() {
		return this.eventUser.contactIds.includes(this.data.uid);
	}

	/**
	 * addToContacts
	 * @param eventUserToAdd
	 * @param event
	 */
	async addToContacts(eventUserToAdd: IEventUser, event: Event) {
		try {
			event.stopPropagation();

			// open card exchange form component
			if (this.cardExchangeForm) {
				// add contact with form
				const modal = await this.modalCtrl.create({
					component: CardExchangeFormComponent,
					componentProps: {
						event: this.event,
						module: this.cardExchangeModule,
						myEventUser: this.eventUser,
						eventUserContact: eventUserToAdd,
						eventUserContactId: eventUserToAdd.uid
					},
					cssClass: this.isMobile ? "card-exhange-form-modal-css-mobile" : "card-exchange-form-modal-css"
				});

				await modal.present();

				const { data } = await modal.onWillDismiss();

				if (!data) {
					return;
				}

				if (data && data?.error) {
					this.SUtility.presentToast(
						this.STranslate.instant("snackbar.error_occured"),
						3000,
						"bottom",
						"error"
					);

					return;
				}

				if (data && data?.success) {
					if (this.eventUser.contactIds && !this.eventUser.contactIds.includes(eventUserToAdd.uid)) {
						this.eventUser.contactIds.push(eventUserToAdd.uid);
					} else {
						this.eventUser = { ...this.eventUser, contactIds: [eventUserToAdd.uid] };
					}

					await this.SEventUsers.updatePartOfEventUser(
						this.event.uid,
						this.eventUser.moduleId,
						this.eventUser.uid,
						{
							contactIds: this.eventUser.contactIds
						}
					);

					this.SUtility.presentToast(
						this.STranslate.instant("snackbar.update_successfull"),
						3000,
						"bottom",
						"success"
					);
				}
			} else {
				await this.storeContactInEventUserDoc(eventUserToAdd.uid);
				this.SUtility.presentToast(
					this.STranslate.instant("snackbar.update_successfull"),
					3000,
					"bottom",
					"success"
				);
			}
		} catch (error) {
			this.eventUserStates[eventUserToAdd.uid] = false;
			this.SUtility.presentToast(this.STranslate.instant("snackbar.error_occured"), 3000, "bottom", "error");
		}
	}

	/**
	 * storeContactInEventUserDoc
	 * @param contactId
	 */
	async storeContactInEventUserDoc(contactId: string) {
		try {
			await this.SCardExchange.storeContactInEventUserDoc(this.event, this.eventUser, contactId);
			this.eventUserStates[contactId] = true;
		} catch (_error) {
			this.snackbar.open(this.STranslate.instant("snackbar.error_occured"), null, {
				duration: 3000,
				panelClass: ["error-snackbar"]
			});
		}
	}

	updateFavoriteAnalytics(add: boolean) {
		if (add) {
			this.SAnalytics.addToFavorites(
				this.event.uid,
				this.favoriteModule.uid,
				this.data.uid,
				this.targetModule.uid,
				TypeModule.ATTENDEE
			);
		} else {
			this.SAnalytics.removeFromFavorites(
				this.event.uid,
				this.favoriteModule.uid,
				this.data.uid,
				this.targetModule.uid,
				TypeModule.ATTENDEE
			);
		}
	}

	getListOptionsDisplayValue(customFieldId: string, selectedOptions: string[]): string[] {
		return this.SCustomFields.getListOptionsDisplayValue(customFieldId, selectedOptions, this.currentLanguage());
	}
}
